import React, { useEffect, useState, useRef } from "react";
import { Container, Row, Col, Modal } from "react-bootstrap";
import TextInput from "../../../../components/formElements/input/TextInput";
import AppointmentIcon from "../../../../components/uiElements/icons/profileIcons/AppointmentIcons";
import { DEFAULT_PIC } from "../../../../constants/image";
import FileAttachment from "../../../../components/formElements/file/FileAttachment";
import { getPreSignedUrl, getWebsocketUrl } from "../../../../utils/urls";
import format from "date-fns/format";
import axios from "axios";
import { getCustomizeFileName } from "../../../../utils/validation/conciergeServiceValidation";
import { useDispatch } from "react-redux";
import { getChatMessagesCountAction, setClearMessageNotificationDot } from "../../../../store/actions";
import "./ChatWindow.css";
import { getDashboardPath } from "../../../../constants";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

const ChatWindow = ({ isOpen = false, chatDetails, setIsOpen=() => {} }) => {
  const [inputValue, setInputValue] = useState("");
  const [messagesArray, setMessagesArray] = useState([]);
  const [socket, setSocket] = useState(null);
  const [pingIntervalState, setPingIntervalState] = useState();
  const [isUploaded, setIsUploaded] = useState(false);
  const [isOpenPreviewimage, setIsOpenPreviewImage] = useState(false);
  const [imageSrc, setImageSrc] = useState();
  const [isFileLoading, setFileLoading] = useState(false);

  const modalRef = useRef(null);
  const dispatch = useDispatch();
  const history = useHistory();

  const userData = JSON.parse(localStorage.getItem("userData"));
  const userAccessToken = localStorage.getItem("token");

  useEffect(() => {
    if (isOpen) {
      handleOpenWebsocketConnection();
    } else {
      if (socket) {
        socket.close();
        setSocket(null);
      }
    }
  
    return () => {
      if (socket) {
        socket.close();
        setSocket(null);
        console.log("WebSocket connection closed on component unmount");
      }
    };
  }, [isOpen]);
  
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // Close WebSocket when the tab is not active
        if (socket && socket.readyState === WebSocket.OPEN) {
          socket.close();
          console.log("WebSocket connection closed due to tab switch");
        }
      } else if (document.visibilityState === "visible" && isOpen) {
        if (socket.readyState !== WebSocket.OPEN) {
          // Reconnect WebSocket when the tab becomes active
          handleOpenWebsocketConnection();
          console.log("WebSocket connection re-established on tab return");
        }
      }
    };
  
    if (isOpen) {
      handleOpenWebsocketConnection();
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
  
    return () => {
      if (socket) {
        socket.close();
        console.log("WebSocket connection closed on component unmount");
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
  

  const handleOpenWebsocketConnection = () => {
    if (socket) {
      socket.close(); // Ensure previous WebSocket is closed
      console.log("Closed previous WebSocket connection before opening a new one");
    }
  
    const ws = new WebSocket(
      getWebsocketUrl(chatDetails?.chat_room, userAccessToken)
    );
  
    ws.onopen = () => {
      console.log("WebSocket connection established");
      ws.send(JSON.stringify({
        type: "read_messages",
        message: inputValue,
      }));

      setTimeout(() => {
        dispatch(setClearMessageNotificationDot({ id: chatDetails?.id }));
        dispatch(getChatMessagesCountAction());
      }, 4000);
    };
  
    ws.onmessage = (event) => {
      const newMessage = JSON.parse(event.data);
      if (newMessage.type === "chat_history") {
        setMessagesArray(newMessage.messages.map((msg) => ({
          ...msg,
          isLoggedInUserMessage: msg.sender_details?.id === userData?.id,
        })));
      } else if (newMessage.type === "chat_message") {
        setMessagesArray((prevMessages) => [
          ...prevMessages,
          {
            ...newMessage,
            isLoggedInUserMessage: newMessage.sender_details?.id === userData?.id,
          },
        ]);
      } else if (newMessage.type === "PING") {
        ws.send(JSON.stringify({ type: 'PONG', message: 'PONG' }));
        console.log('Sent PONG response');
      }
    };
  
    ws.onclose = () => console.log("WebSocket connection closed");
    ws.onerror = (error) => console.error("WebSocket error: ", error);
  
    setSocket(ws); // Save the WebSocket instance
  };
  

  const handleCloseConnection = () => {
    setMessagesArray([]);
    
    if (pingIntervalState) {
      clearInterval(pingIntervalState); 
      setPingIntervalState(null);
    }
  
    if (socket) {
      socket.close(); // Properly close WebSocket
      setSocket(null); // Reset socket state
      console.log("WebSocket connection manually closed");
    }
  
    setIsOpen(false); // Close the modal
  };
  

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.scrollTo({
        top: modalRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messagesArray]);

  const sanitizeAndTimestampFileName = (fileName) => {
    // Get the current timestamp
    const {fileBaseName, fileExtension, fileTimestamp} = getCustomizeFileName(fileName)
 
    // Combine sanitized name, timestamp, and extension
    const sanitizedFileName = `${fileBaseName}_${fileTimestamp}${fileExtension}`;

    return sanitizedFileName;
}

const handleGetPreSignedUrl = async (fileName) => {
  if (!isUploaded) {
    try {
        const sanitizedFileName = sanitizeAndTimestampFileName(fileName);
        const customFullFilePath = `/${chatDetails?.chat_room}/${userData?.user}/${sanitizedFileName}`;
        
        const response = await axios.get(getPreSignedUrl(customFullFilePath));

        setIsUploaded(true);
  
        return {
          signedUrl: response.data.data.signed_url,
          fullFilePath: customFullFilePath
        }; 
    } catch (error) {
        console.error("Error fetching pre-signed URL:", error.message);
        throw error; // Propagate the error for further handling
    }
  }
};

  const handleUploadFile = async (fileObject) => {
    setFileLoading(true);
    if (!isUploaded) {
      try {
          const fileObjectData = fileObject.chat_after_call_doc[0];
          const {signedUrl, fullFilePath} = await handleGetPreSignedUrl(fileObjectData?.name); // Await URL fetching
          const response = await axios.put(signedUrl, fileObjectData, {
            headers: {
                "Content-Type": fileObjectData.type,
                "Access-Control-Allow-Origin": "*",
                "Access-Control-Allow-Methods": "POST, PUT, DELETE, GET, OPTIONS",
                "Access-Control-Request-Method": "*",
                "Access-Control-Allow-Headers": "Origin, X-Requested-With, Content-Type, Accept, Authorization",
            },
        });
  
        sendFileToWebsocket(fileObjectData.type, fullFilePath);
          if (response) {
            console.log("File uploaded successfully.");
  
          } else {
            console.error("File upload failed:");
          }
          setIsUploaded(false);
        } catch (error) {
          console.error("Error uploading file:",error);
          setIsUploaded(false);
        }
    }
    setFileLoading(false);
  };

  const sendFileToWebsocket = (contentType, filePath) => {

    let content_type = 'IMAGE'
    if (contentType.includes("pdf")) {
      content_type = 'PDF'
    }
    const sendFileMessage = JSON.stringify({
      type: "chat_message",
      link: filePath,
      link_type: content_type
    });
    socket.send(sendFileMessage);
  };

  const handleSendChatMessage = (e) => {
    e?.preventDefault();
    if (inputValue?.length) {
      if (socket && socket.readyState === WebSocket.CLOSED) {
        handleOpenWebsocketConnection();
      }
      if (socket && socket.readyState === WebSocket.OPEN) {
        const sendChatMessage = JSON.stringify({
          type: "chat_message",
          message: inputValue,
        });
        socket.send(sendChatMessage);
        setInputValue("");
      } else {
        console.error("WebSocket is not connected.");
      }
    }
  };

  const handleDateTimeForMessage = (timestampInSeconds) => {

    const date = new Date((timestampInSeconds * 1000)); // Adjust for user's timezone
    const now = new Date();

    if (date.getFullYear() < now.getFullYear()) {
      // Show year if it's not the current year
      return format(date, "MMM dd, yyyy hh:mm a");
    } else if (date.getDate() < now.getDate()) {
      // Show date if it's from previous day
      return format(date, "MMM dd, hh:mm a");
    } else {
      // Otherwise, show only time
      return format(date, "hh:mm a");
    }
  }

  return (
    <Modal
      show={isOpen}
      aria-labelledby="contained-modal-title-vcenter"
      style={{
        inset: "4% auto auto 32%",
      }}
    >
      <Modal.Header style={{ height: "66px" }}>
        <Modal.Title>
          <p 
          className="cursor-pointer"
          onClick={()=> {
            handleCloseConnection();
            history.push({
              pathname: getDashboardPath(`/patients/detail/${chatDetails?.patient?.details_id}`),
              state:{pop:true}
            });
          }}
          style={{ textAlign: "left" }}>
            <img
              src={chatDetails?.patient?.image || DEFAULT_PIC}
              className="profile-image-secondary"
              alt="Profile"
            />
            <span className="patient-name">{chatDetails?.patient?.name}</span>
          </p>
        </Modal.Title>
        <button onClick={handleCloseConnection} className="close-chat fileClose-chat" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </Modal.Header>
      <Modal.Body
        className="show-grid"
        scrollable={true}
        style={{
          maxHeight: "calc(100vh - 210px)",
          overflowY: "auto",
          height: "590px",
        }}
        ref={modalRef}
      >
        <Container fluid="md">
          {messagesArray.map((message, index) => (
            <Row key={index}>
              <Col>
                <div
                  style={{
                    marginTop: "3px",
                    float: message?.sender_details?.id === userData?.user ? "right" : "left",
                    textAlign: message?.sender_details?.id === userData?.user ? "right" : "left",
                  }}
                >
                  <span style={{ fontSize: "13px" }}>
                  {handleDateTimeForMessage(message?.message_details?.timestamp)}
                  </span>
                  <br />
                  <div
                  className="chat-message-container"
                    style={{
                        borderRadius: message?.sender_details?.id === userData?.user ? "7px 0px 7px 7px" : "0px 7px 7px 7px",
                        color: "var(--icon-color)",
                        float: message?.sender_details?.id === userData?.user ? "right": '',
                    }}
                  >
                    {message?.message_details?.link ? ( message?.message_details?.link_type === "PDF" ? (
                    <a
                    href={message?.message_details?.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    >
                      View PDF
                      </a>
                      ) : (
                        <a
                    href={message?.message_details?.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    >
                      <img
                      onClick={()=> {
                        setImageSrc(message?.message_details?.link);
                        setIsOpenPreviewImage(true);
                      }}
                      src={message?.message_details?.link}
                      alt="Image Preview"
                      style={{ maxWidth: "150px", maxHeight: "150px", borderRadius: "5px" }}/></a>
                      )) : (
                        <div>
                          {message?.message_details?.message}
                        </div>
                        )}
                  </div>
                </div>
              </Col>
            </Row>
          ))}
        </Container>
      </Modal.Body>
      <Modal.Footer style={{ backgroundColor: "gainsboro", height: "66px" }}>
        <Container>
          <Row>
            <Col className="cursor-pointer" xs lg="2">
              <FileAttachment onChange={(fileObject) => {
                handleUploadFile(fileObject)
                }} name="chat_after_call_doc"
                isLoading={isFileLoading} />
            </Col>
            <Col xs={12} md={8}>
              <TextInput
                autoFocus
                placeholder="Message..."
                className="chat-box-after-call"
                value={inputValue}
                onChange={(val) => setInputValue(val)}
                onKey={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault(); 
                      handleSendChatMessage();
                    }
                  }}
              />
            </Col>
            <Col xs lg="2">
              <AppointmentIcon
                className="chat-msg-send"
                type="msgSend"
                onClick={handleSendChatMessage}
              />
            </Col>
          </Row>
        </Container>
      </Modal.Footer>
    </Modal>
  );
};

export default React.memo(ChatWindow);
